/**
 * Flag lifecycle wire contracts (PRD F1–F2; increment-8 plan §1 PR2).
 *
 * The flag VOCABULARY and its thresholds are not here — they are versioned
 * clinical data in `@advaitnetra/protocol` (flags.ts), PROPOSED pending
 * clinical sign-off (OQ4). This module carries only what two surfaces must
 * agree on: the request/response shapes and the evidence envelope.
 *
 * Two rules the shapes themselves enforce, because a contract that permits a
 * bad act invites one:
 *
 *  1. **A raise over the wire is always MANUAL.** There is no request shape a
 *     client could use to mint a system flag — auto raises exist only inside
 *     the two gated evaluation moments, server-side. The code enum here is the
 *     manual pair, not the six.
 *  2. **A resolution needs BOTH action and resolution**, non-empty. F2 states
 *     two facts ("requires a human action, and closes only with a
 *     human-entered resolution"), and accepting one without the other is how a
 *     UI drifts into an acknowledge step — which no adopted source has.
 */

import { z } from 'zod';

/** The six codes, restated as a wire enum. Source of truth for what they MEAN is @advaitnetra/protocol. */
export const flagCodeWireSchema = z.enum([
  'PAIN_AGG',
  'EXT_DEF',
  'ADHER_70',
  'FUNC_DELAY',
  'SWELL_INC',
  'SLR_CTRL',
]);
export type FlagCodeWire = z.infer<typeof flagCodeWireSchema>;

/** Only these two are raisable by a human — the whole point of the manual/auto split. */
export const manualFlagCodeSchema = z.enum(['SWELL_INC', 'SLR_CTRL']);
export type ManualFlagCode = z.infer<typeof manualFlagCodeSchema>;

export const flagSeverityWireSchema = z.enum(['info', 'warning']);
export const flagRaisedBySchema = z.enum(['system', 'clinician']);

/**
 * What a raise read and what it computed. `kind` says which gated moment (or
 * that a human raised it); `refs` names the records; `values` carries the
 * rule's own computed numbers, verbatim from the protocol rule verdict.
 */
export const flagEvidenceSchema = z.object({
  kind: z.enum(['session-sign-off', 'assessment-filing', 'manual']),
  refs: z.object({
    sessionId: z.string().nullable().optional(),
    assessmentId: z.string().nullable().optional(),
    observationId: z.string().nullable().optional(),
  }),
  /** The rule verdict's `values` object; empty for a manual raise, which has no arithmetic. */
  values: z.record(z.string(), z.unknown()),
  /**
   * Always true while OQ4 is open: every auto rule's thresholds are PROPOSED.
   * Stored on the evidence so a surface cannot render a raise without the
   * marking, the same guarantee the assessment target pills carry.
   */
  thresholdsProposed: z.boolean(),
});
export type FlagEvidence = z.infer<typeof flagEvidenceSchema>;

export const flagResolutionSchema = z.object({
  id: z.string(),
  flagEventId: z.string(),
  action: z.string(),
  resolution: z.string(),
  occurredAt: z.string(),
  recordedAt: z.string(),
  actor: z.string(),
});
export type FlagResolutionRecord = z.infer<typeof flagResolutionSchema>;

export const flagEventSchema = z.object({
  id: z.string(),
  episodeId: z.string(),
  code: flagCodeWireSchema,
  severity: flagSeverityWireSchema,
  raisedBy: flagRaisedBySchema,
  evidence: flagEvidenceSchema,
  ruleVersion: z.string(),
  note: z.string().nullable(),
  occurredAt: z.string(),
  recordedAt: z.string(),
  actor: z.string(),
  /** Null while OPEN. Presence of this object IS closure — there is no status enum. */
  resolution: flagResolutionSchema.nullable(),
});
export type FlagEventRecord = z.infer<typeof flagEventSchema>;

/**
 * Raise a flag by hand. Manual codes only, and the note is REQUIRED: a
 * physio raising a flag on their own judgement says why in their own words,
 * because `evidence` — which carries an auto rule's reasoning — has nothing
 * to say about a human's.
 */
export const raiseFlagRequestSchema = z.object({
  code: manualFlagCodeSchema,
  note: z.string().trim().min(1).max(2000),
  occurredAt: z.string().datetime({ offset: true }),
  /** Optional linkage to the session the physio was in when they raised it. */
  sessionId: z.string().nullable().optional(),
});
export type RaiseFlagRequest = z.infer<typeof raiseFlagRequestSchema>;

/** Close a flag. Both fields required and non-empty — see the module note. */
export const resolveFlagRequestSchema = z.object({
  action: z.string().trim().min(1).max(2000),
  resolution: z.string().trim().min(1).max(2000),
  occurredAt: z.string().datetime({ offset: true }),
});
export type ResolveFlagRequest = z.infer<typeof resolveFlagRequestSchema>;

export const flagResponseSchema = z.object({ ok: z.literal(true), flag: flagEventSchema });
export const episodeFlagsResponseSchema = z.object({
  ok: z.literal(true),
  episodeId: z.string(),
  flags: z.array(flagEventSchema),
});
